import Sqids from "sqids";

/**
 * ID Obfuscation Utility using Sqids
 *
 * This utility provides encoding/decoding of numeric IDs to make URLs harder to guess.
 * IMPORTANT: This is NOT a security measure - it's obfuscation only.
 * Backend authorization checks are still required.
 *
 * Usage:
 *   encode(123) => "K4x8hM"
 *   decode("K4x8hM") => 123
 */

// Use a custom alphabet for shorter, URL-safe IDs
// IMPORTANT: Do not change this alphabet once in production, or all existing encoded IDs will break
const ALPHABET =
  "frBd2HkWNAh1oIVEYcj49G5xLt0PJRCKSaQey6zwpZqinsl3g7TD8vbuMFUXmO";

// Minimum length for encoded IDs (makes them harder to guess)
const MIN_LENGTH = 8;

const sqids = new Sqids({
  alphabet: ALPHABET,
  minLength: MIN_LENGTH,
});

/**
 * Encode a numeric ID to an obfuscated string
 * @param id - The numeric ID to encode
 * @returns The encoded string
 */
export function encodeId(id: number): string {
  if (id < 0 || !Number.isInteger(id)) {
    console.warn(
      "encodeId: Invalid ID provided, must be a non-negative integer",
    );
    return String(id);
  }
  return sqids.encode([id]);
}

/**
 * Decode an obfuscated string back to a numeric ID
 * @param encoded - The encoded string
 * @returns The decoded numeric ID, or null if invalid
 */
export function decodeId(encoded: string): number | null {
  if (!encoded || typeof encoded !== "string") {
    return null;
  }

  try {
    const decoded = sqids.decode(encoded);
    if (decoded.length === 0) {
      return null;
    }
    return decoded[0];
  } catch {
    return null;
  }
}

/**
 * Encode multiple IDs at once (useful for composite keys)
 * @param ids - Array of numeric IDs
 * @returns The encoded string
 */
export function encodeIds(ids: number[]): string {
  const validIds = ids.filter((id) => id >= 0 && Number.isInteger(id));
  if (validIds.length !== ids.length) {
    console.warn("encodeIds: Some invalid IDs were filtered out");
  }
  return sqids.encode(validIds);
}

/**
 * Decode a string back to multiple IDs
 * @param encoded - The encoded string
 * @returns Array of decoded numeric IDs
 */
export function decodeIds(encoded: string): number[] {
  if (!encoded || typeof encoded !== "string") {
    return [];
  }

  try {
    return sqids.decode(encoded);
  } catch {
    return [];
  }
}

/**
 * Decode an obfuscated string back to a numeric ID (strict mode)
 * Rejects non-canonical encodings by re-encoding and comparing
 * @param encoded - The encoded string
 * @returns The decoded numeric ID, or null if invalid or non-canonical
 */
export function decodeIdStrict(encoded: string): number | null {
  const id = decodeId(encoded);
  if (id === null) {
    return null;
  }

  // Re-encode and verify it matches the original
  if (encodeId(id) !== encoded) {
    return null;
  }

  return id;
}

/**
 * Check if a string is a valid encoded ID
 * @param encoded - The string to check
 * @returns True if the string can be decoded
 */
export function isValidEncodedId(encoded: string): boolean {
  return decodeId(encoded) !== null;
}

/**
 * Safely parse a route parameter that might be encoded or a raw number
 * Useful during migration period when both formats might exist
 * @param param - The route parameter (encoded string or numeric string)
 * @returns The numeric ID, or null if invalid
 */
export function parseRouteId(param: string | undefined): number | null {
  if (!param) {
    return null;
  }

  // First, check if it's a raw number (for backwards compatibility with old links)
  // This must come first because sqids.decode() will decode ANY alphanumeric string
  const asNumber = parseInt(param, 10);
  if (!isNaN(asNumber) && asNumber >= 0 && String(asNumber) === param) {
    return asNumber;
  }

  // Then try to decode as sqids (using strict mode to verify canonical encoding)
  const decoded = decodeIdStrict(param);
  if (decoded !== null) {
    return decoded;
  }

  return null;
}